// Standalone strain-reviews — entry point.
//
// Renders the EXACT same popup the chatbot shows ("Infographic v2"), by
// reusing the real renderer from ../features/strain_info.js. We just give
// it a standalone modal host to mount into instead of the chat widget.
//
//   1. Inject host/backdrop CSS once (popup internals styled by strain_info).
//   2. Delegate clicks on [data-strain-name] anywhere on the host page.
//   3. Expose window.VBStrainReviews.open(name) for programmatic use.
//   4. On open: build a centered modal host, fetch the strain payload, hand
//      it to openStrainOverlay() as preloadedData (so it renders without a
//      second fetch), and wire backdrop / Esc / X to tear everything down.

import { injectStyles } from './styles.js';
import { fetchStrainReviews, fetchStrainReviewsConfig, postStrainReviewView, fetchSourceCounts } from './api.js';
import { API_URL, MODAL_ID, TRIGGER_SELECTOR } from './config.js';
import { el } from './dom.js';
import { startMenuAutoInject, updateButtonCounts, normStrainName, readCachedSourceCount } from './menu-adapter.js';
import {
    openStrainOverlay,
    ensureStrainOverlayStyles,
} from '../features/strain_info.js';


let _openName = '';

// Server-resolved per-store config (enabled flag + button text + onlyPaths).
// Populated by init() before any auto-inject runs. FAIL CLOSED: if this stays
// null the widget never wires up.
let _resolvedCfg = null;


function _teardown() {
    document.querySelectorAll('.vbsr-host, .vbsr-backdrop').forEach(n => {
        if (n.parentNode) n.parentNode.removeChild(n);
    });
    document.removeEventListener('keydown', _onEsc);
    _openName = '';
}


function _onEsc(e) {
    if (e.key === 'Escape') _teardown();
}


async function openModal(strainName) {
    if (!strainName || typeof strainName !== 'string') return;
    _teardown();                 // hard reset — never stack modals
    _openName = strainName;

    injectStyles();
    ensureStrainOverlayStyles(); // popup's own CSS (idempotent)

    const backdrop = el('div', {
        class: 'vbsr-backdrop',
        on: { click: _teardown },
    });
    const host = el('div', { class: 'vbsr-host', id: 'vbsr-host' });
    const closeBtn = el('button', {
        class: 'vbsr-host-close',
        type: 'button',
        'aria-label': 'Close',
        on: { click: _teardown },
    }, ['×']);
    host.appendChild(closeBtn);

    document.body.appendChild(backdrop);
    document.body.appendChild(host);
    document.addEventListener('keydown', _onEsc);

    // Fire-and-forget analytics (separate from the chatbot's own telemetry).
    postStrainReviewView(strainName);

    // Fetch our payload first, then hand it to the real renderer as
    // preloadedData so it renders the chatbot popup WITHOUT a second fetch.
    // A null payload becomes a minimal empty row → the renderer shows its
    // own "Strain details not available yet" state (same as in-chat).
    const data = await fetchStrainReviews(strainName);

    // The renderer mounts its overlay into `widget.chatWidget`. We point that
    // at our host so the popup fills the modal frame exactly like it fills
    // the chat panel. API_BASE drives the lineage-recenter + telemetry calls.
    const widgetShim = {
        chatWidget: host,
        API_BASE: API_URL,
        // §9 — recordStrainEvent (thumbs up/down) reads _vbBusinessId(widget) =
        // widget.businessId || window.CHATBOT_BUSINESS_ID. The standalone shim
        // previously provided neither, so menu-popup feedback never POSTed
        // (UI showed "Thanks" but nothing recorded). Supply the business id so
        // the "Was this helpful?" telemetry attributes correctly on the
        // standalone menu popup too.
        businessId: (window.VB_STRAIN_REVIEWS_CONFIG && window.VB_STRAIN_REVIEWS_CONFIG.BUSINESS_ID) || null,
        themeEnabled: false,
    };

    await openStrainOverlay(
        widgetShim,
        null,                                    // productId — analytics only
        strainName,                              // sticky-bar title until canonical resolves
        data || { name: strainName, mega_source_count: 0 },
        undefined,                               // initialActiveKey — default primary
        { showFaq: true, stackRadarLabels: true }, // §3 popup SHOWS faqs; §10 stacked radar labels are popup-only (chatbot keeps single line)
    );

    // The popup's own sticky-bar "Back" button calls overlay.remove(), which
    // would leave our host + backdrop behind. Patch the overlay's remove to
    // tear the whole modal down so Back closes everything cleanly.
    const overlay = host.querySelector('.vb-strain-overlay');
    if (overlay) {
        const _origRemove = overlay.remove.bind(overlay);
        overlay.remove = function () {
            _origRemove();
            _teardown();
        };
    }

    // §6 — Branding footer (lead-gen). STANDALONE MENU POPUP ONLY. This must
    // NEVER live in the shared strain_info.js renderer (the in-chat / paid
    // chatbot popup must not get it). The free menu popup doubles as a lead
    // channel: a "Powered by VirtualBudz" credit + an "Add free strain
    // insights to your menu →" CTA. Appended to the popup here so every embed
    // gets it (not just our Vercel proxy). Styling mirrors strain-menu-proxy
    // middleware.js BRAND_FOOTER_JS / BRAND_MOBILE_CSS.
    _appendBrandFooter(host);
}


// Append the standalone lead-gen branding footer to the popup. Idempotent
// (guarded by .vbsr-brand-footer). Config-driven with VirtualBudz defaults:
// brandLink / brandCtaText / brandCtaUrl resolved from the server config
// (_resolvedCfg) if present, else the inline window.VB_STRAIN_REVIEWS_CONFIG,
// else the VirtualBudz defaults. NOTE: the backend
// /admin/public/strain-reviews-config payload does NOT yet return
// brandLink/brandCtaText/brandCtaUrl — until it does, defaults are used.
const _BRAND_DEFAULTS = {
    brandLink: 'https://virtualbudz.com',
    brandCtaText: 'Add free strain insights to your menu →',
    brandCtaUrl: 'https://virtualbudz.com',
};

function _resolveBrandCfg() {
    const inline = (typeof window !== 'undefined' && window.VB_STRAIN_REVIEWS_CONFIG) || {};
    const server = _resolvedCfg || {};
    const pick = (key) => {
        if (typeof server[key] === 'string' && server[key]) return server[key];
        if (typeof inline[key] === 'string' && inline[key]) return inline[key];
        return _BRAND_DEFAULTS[key];
    };
    return {
        brandLink: pick('brandLink'),
        brandCtaText: pick('brandCtaText'),
        brandCtaUrl: pick('brandCtaUrl'),
    };
}

function _appendBrandFooter(host) {
    if (!host) return;
    // Prefer appending to the popup itself so the footer scrolls with content;
    // fall back to the host frame if the popup node isn't found.
    const target = host.querySelector('.vb-pop--info2') || host;
    if (!target || target.querySelector('.vbsr-brand-footer')) return; // idempotent

    const cfg = _resolveBrandCfg();
    const footer = el('div', { class: 'vbsr-brand-footer' }, [
        el('a', {
            class: 'vbsr-brand-credit',
            href: cfg.brandLink,
            target: '_blank',
            rel: 'noopener',
        }, [
            'Powered by ',
            el('b', {}, ['VirtualBudz']),
        ]),
        el('a', {
            class: 'vbsr-brand-cta',
            href: cfg.brandCtaUrl,
            target: '_blank',
            rel: 'noopener',
        }, [cfg.brandCtaText]),
    ]);
    target.appendChild(footer);
}


function _onDocumentClick(e) {
    let node = e.target;
    while (node && node !== document) {
        if (node.matches && node.matches(TRIGGER_SELECTOR)) {
            e.preventDefault();
            openModal(node.getAttribute('data-strain-name'));
            return;
        }
        node = node.parentNode;
    }
}


async function init() {
    if (window.__vbStrainReviewsInit) return;
    window.__vbStrainReviewsInit = true;

    const cfg = (typeof window !== 'undefined' && window.VB_STRAIN_REVIEWS_CONFIG) || {};
    const businessId = cfg.BUSINESS_ID;

    // FAIL CLOSED: resolve the per-store config from the server first. If we
    // can't (no business id, fetch error, or the store isn't enabled), publish
    // a NO-OP API and do NOT wire up clicks or menu auto-inject.
    const serverCfg = businessId ? await fetchStrainReviewsConfig(businessId) : null;
    _resolvedCfg = serverCfg
        ? {
            enabled: serverCfg.enabled === true,
            buttonText: typeof serverCfg.buttonText === 'string' ? serverCfg.buttonText : null,
            onlyPaths: serverCfg.onlyPaths,
            // §6 — per-store branding-footer overrides. The backend
            // /admin/public/strain-reviews-config payload does NOT yet return
            // these (separate follow-up); until it does they stay null and the
            // footer uses the VirtualBudz defaults in _resolveBrandCfg().
            brandLink: typeof serverCfg.brandLink === 'string' ? serverCfg.brandLink : null,
            brandCtaText: typeof serverCfg.brandCtaText === 'string' ? serverCfg.brandCtaText : null,
            brandCtaUrl: typeof serverCfg.brandCtaUrl === 'string' ? serverCfg.brandCtaUrl : null,
        }
        : null;

    if (!businessId || !_resolvedCfg || _resolvedCfg.enabled !== true) {
        window.VBStrainReviews = {
            open() {},
            close() {},
            rescan() {},
        };
        return;
    }

    injectStyles();
    _businessId = businessId;
    document.addEventListener('click', _onDocumentClick);
    window.VBStrainReviews = {
        open: openModal,
        close: _teardown,
        rescan: () => { startMenuAutoInject(_autoInjectOpts()); _scheduleResolveCounts(); },
    };

    // Auto-inject trigger buttons onto recognized dispensary menus (Dutchie
    // inline cards, etc.). Customers who only want the manual [data-strain-name]
    // button can disable this via window.VB_STRAIN_REVIEWS_CONFIG.autoInject=false.
    if (cfg.autoInject !== false) {
        startMenuAutoInject(_autoInjectOpts());
        // After the first inject pass, resolve the real source counts for the
        // strains now on the page and rewrite the button labels. Then watch the
        // DOM (debounced) so late-rendered cards (SPA nav, infinite scroll, the
        // menu-adapter's own MutationObserver) also get their counts.
        _scheduleResolveCounts();
        _startCountObserver();
    }
}

// The active store's business_id, captured at init() so the count resolver +
// observer can read it without re-deriving from config.
let _businessId = null;

// Resolve + apply source counts for every injected button currently on the
// page. Cache-first: seed labels from sessionStorage immediately, then fetch
// ONLY the uncached strain names in one round-trip and re-apply. The button
// template comes from _autoInjectOpts() (server/inline override or the default
// {count} template). A static (no-{count}) template short-circuits cheaply.
let _resolveInFlight = false;
async function _resolveSourceCounts() {
    if (_resolveInFlight) return;          // collapse overlapping passes
    const opts = _autoInjectOpts();
    const template = opts.buttonText;
    if (typeof template !== 'string' || template.indexOf('{count}') === -1) return;
    if (!_businessId) return;

    // Collect the unique original strain names currently on the page.
    const btns = document.querySelectorAll('.vbsr-inject-btn');
    if (!btns.length) return;
    const origByNorm = {};                 // norm -> original spelling (one is fine)
    btns.forEach(function (btn) {
        const name = btn.getAttribute('data-strain-name') || '';
        const norm = normStrainName(name);
        if (norm && !(norm in origByNorm)) origByNorm[norm] = name;
    });
    const norms = Object.keys(origByNorm);
    if (!norms.length) return;

    // Seed from the sessionStorage cache first so already-known labels update
    // instantly without waiting on the network.
    const cached = {};
    const missingNames = [];
    norms.forEach(function (norm) {
        const c = readCachedSourceCount(norm);
        if (Number.isFinite(c)) cached[norm] = c;
        else missingNames.push(origByNorm[norm]);
    });
    if (Object.keys(cached).length) updateButtonCounts(cached, template);
    if (!missingNames.length) return;

    _resolveInFlight = true;
    try {
        // Backend keys the response by the ORIGINAL name we sent; re-key by
        // normalized name so updateButtonCounts (which normalizes each button's
        // data-strain-name) can match. updateButtonCounts also writes the
        // resolved counts back into sessionStorage.
        const data = await fetchSourceCounts(_businessId, missingNames);
        const byNorm = {};
        Object.keys(data || {}).forEach(function (origName) {
            const norm = normStrainName(origName);
            const n = data[origName];
            if (norm && Number.isFinite(n)) byNorm[norm] = n;
        });
        if (Object.keys(byNorm).length) updateButtonCounts(byNorm, template);
    } catch (_e) {
        /* fallback labels stay; never throw */
    } finally {
        _resolveInFlight = false;
    }
}

// Debounced scheduler so a burst of DOM mutations triggers a single resolve.
let _resolveDeb = null;
function _scheduleResolveCounts() {
    if (_resolveDeb) clearTimeout(_resolveDeb);
    _resolveDeb = setTimeout(function () { _resolveSourceCounts(); }, 350);
}

// Re-resolve counts whenever new buttons appear (the menu-adapter's own
// observer injects them on SPA nav / infinite scroll). Idempotent — one
// observer per page.
function _startCountObserver() {
    if (window.__vbsrCountObserver) return;
    const obs = new MutationObserver(function () { _scheduleResolveCounts(); });
    obs.observe(document.body, { childList: true, subtree: true });
    window.__vbsrCountObserver = obs;
}

function _autoInjectOpts() {
    const cfg = (typeof window !== 'undefined' && window.VB_STRAIN_REVIEWS_CONFIG) || {};
    // Prefer the server-resolved onlyPaths, else the inline config. Coerce to a
    // non-empty array or null (an empty/invalid value means "no path filter").
    const rawPaths = (_resolvedCfg && _resolvedCfg.onlyPaths != null)
        ? _resolvedCfg.onlyPaths
        : cfg.onlyPaths;
    const onlyPaths = Array.isArray(rawPaths) && rawPaths.length ? rawPaths : null;
    // Prefer the server-resolved button text, else the inline config, else the
    // default dynamic template. The {count} placeholder is filled in by
    // updateButtonCounts() once the strain's real source_count resolves; until
    // then makeButton renders the neutral "See what online sources say"
    // fallback (never a wrong/placeholder number). A configured buttonText
    // (server or inline) still overrides this — and may omit {count} for a
    // static label.
    const buttonText =
        (_resolvedCfg && typeof _resolvedCfg.buttonText === 'string' && _resolvedCfg.buttonText)
        || (typeof cfg.buttonText === 'string' ? cfg.buttonText : null)
        || 'Check what {count} sources say about this strain';
    return {
        injectAll: cfg.injectAll === true,
        buttonText: buttonText,
        onlyPaths: onlyPaths,
    };
}


if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
}
